import type { ReactNode } from 'react';
import { Bot, Lightbulb, Plug, Settings, PanelLeftClose, LogOut, Plus, Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SessionHistoryList } from '@/components/sessions/SessionHistoryList';
import { getStoredTheme, setTheme, type ThemeMode } from '@/lib/theme';
import { useState, useEffect } from 'react';
import HAIAssistantsLogo from '@/components/ui/hai-assistants-logo';

interface AppSidebarProps {
  open: boolean;
  currentView: 'agent-selection' | 'agent-editor' | 'chat' | 'skills' | 'settings' | 'connectors';
  currentSessionId?: string | null;
  workspaceId: string | null;
  onClose: () => void;
  onNavigate: (view: 'agent-selection' | 'skills' | 'connectors' | 'settings') => void;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
  onSignOut: () => void;
}

export function AppSidebar({
  open,
  currentView,
  currentSessionId,
  workspaceId,
  onClose,
  onNavigate,
  onSessionSelect,
  onNewChat,
  onSignOut,
}: AppSidebarProps) {
  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-40 w-72 border-r border-border bg-background/95 backdrop-blur flex flex-col transform transition-transform duration-200 ease-out shadow-lg',
        open ? 'translate-x-0' : '-translate-x-full'
      )}
      aria-hidden={!open}
    >
      <div className="flex items-center justify-between p-3 border-b border-border">
        <div className="flex items-center gap-2">
          <HAIAssistantsLogo variant="with-presidio" height={24} />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="size-7"
          title="Hide sidebar"
        >
          <PanelLeftClose className="size-4" strokeWidth={2.2} />
        </Button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        <div className="p-2 space-y-1">
          <SidebarNavButton
            active={currentView === 'agent-selection'}
            icon={<Bot className="size-3.5" />}
            label="Agents"
            onClick={() => onNavigate('agent-selection')}
          />
          <SidebarNavButton
            active={currentView === 'skills'}
            icon={<Lightbulb className="size-3.5" />}
            label="Skills"
            onClick={() => onNavigate('skills')}
          />
          <SidebarNavButton
            active={currentView === 'connectors'}
            icon={<Plug className="size-3.5" />}
            label="Connectors"
            onClick={() => onNavigate('connectors')}
          />
          <SidebarNavButton
            active={currentView === 'settings'}
            icon={<Settings className="size-3.5" />}
            label="Settings"
            onClick={() => onNavigate('settings')}
          />
        </div>

        <div className="px-3 pt-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
          Chats
        </div>
        <div className="p-2">
          <Button
            onClick={onNewChat}
            className="w-full justify-start gap-2 text-xs h-8"
            size="sm"
          >
            <Plus className="size-4" />
            New Chat
          </Button>
        </div>

        <SessionHistoryList
          className="flex-1"
          workspaceId={workspaceId}
          currentSessionId={currentSessionId ?? undefined}
          visible={open}
          onSessionSelect={onSessionSelect}
        />
      </div>

      <div className="p-3 border-t border-border space-y-2">
        <ThemeToggle />
        <Button
          variant="outline"
          className="w-full justify-start gap-2 text-xs"
          onClick={onSignOut}
        >
          <LogOut className="size-3.5" />
          Sign out
        </Button>
      </div>
    </aside>
  );
}

function SidebarNavButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2 px-2 py-2 rounded-md text-xs transition-colors',
        active ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-accent/50 text-foreground'
      )}
    >
      {icon}
      <span className="text-left">{label}</span>
    </button>
  );
}

function ThemeToggle() {
  const [theme, setThemeState] = useState<ThemeMode>('system');

  useEffect(() => {
    setThemeState(getStoredTheme());
  }, []);

  const handleThemeChange = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    setTheme(newTheme);
  };

  const themeConfig = {
    light: { icon: Sun, label: 'Light mode' },
    dark: { icon: Moon, label: 'Dark mode' },
    system: { icon: Monitor, label: 'System theme' },
  };

  return (
    <div className="flex items-center justify-center gap-1 p-1 rounded-md bg-muted/40">
      {(Object.keys(themeConfig) as ThemeMode[]).map((mode) => {
        const { icon: Icon, label } = themeConfig[mode];
        return (
          <button
            key={mode}
            type="button"
            onClick={() => handleThemeChange(mode)}
            title={label}
            className={cn(
              'flex-1 p-1.5 rounded transition-all',
              theme === mode
                ? 'bg-background shadow-sm text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
            )}
          >
            <Icon className="size-3.5 mx-auto" strokeWidth={2} />
          </button>
        );
      })}
    </div>
  );
}
